import { KatapultElement, html } from '../../mixins/katapult-element.js';
import { KatapultDialog } from '../katapult-elements/katapult-dialog.js';
import '@polymer/paper-toggle-button/paper-toggle-button.js';
import '@polymer/iron-collapse/iron-collapse.js';
import '../paper-header/paper-header.js';
import '../style-modules/flex.js';
import { Convert } from '../../modules/Convert.js';
import { snakeCase } from 'change-case';

/** Element to display and edit company authentication options */
class CompanyAuthenticationOptions extends KatapultElement {
  static get template() {
    return html`
    <style include="flex">
    :host {
      /* TODO: Add HOST styles here */
    }
    [hidden] {
      display: none !important;
    }
    div[slot="header"] {
      display: flex;
      flex-grow: 1;
      align-items: center;
      box-sizing: border-box;
      height: 100%;
      padding: 12px;
      cursor: pointer;
    }
    div[slot="content"] > div {
      padding: 16px 12px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      border-bottom: 1px solid var(--paper-grey-200);
    }
    .option-description {
      font-size: 14px;
    }
    .slim-input {
      width: 80px;
    }
    #signInMethods {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    #signInMethods > paper-toggle-button {
      margin-left: 12px;
    }
    </style>

    <paper-header-content>
      <div slot="header" style="cursor: pointer; gap: var(--sl-spacing-x-small);">
        <katapult-icon icon="security"></katapult-icon>
        <span>Authentication</span>
      </div>
      <div slot="content" flex column>
        <div>
          <div flex align-center space-between>
            <div>Multi-Factor Authentication</div>
            <paper-toggle-button hidden$="[[mfaAutoEnforced]]" checked="[[mfaEnabled]]" on-click="promptToggleMfa"></paper-toggle-button>
            <paper-toggle-button hidden$="[[!mfaAutoEnforced]]" checked disabled></paper-toggle-button>
          </div>
          <div class="option-description" hidden$="[[mfaAutoEnforced]]">
            Require all users to authenticate with a second factor (email confirmation code) when logging in.<br/><br/>
            Multi-factor authentication is automatically enforced for company admins.
          </div>
          <div class="option-description" hidden$="[[!mfaAutoEnforced]]">
            Multi-factor authentication is automatically enforced for all users in this company.
          </div>
        </div>
        <div>
          <div flex align-center space-between>
            <div>Password Expiration Policy</div>
            <paper-toggle-button id="usePasswordExpirationInput" checked="[[usePasswordExpiration]]"
              on-click="toggleUsePasswordExpiration"
            ></paper-toggle-button>
          </div>
          <div class="option-description">Require users to change their password after a certain number of days.</div>
          <div class="option-description" hidden$="[[!serverPasswordExpireTime]]">
            This server has a policy of expiring passwords automatically after [[_toDays(serverPasswordExpireTime)]] days.
            You may set a lower value here, but not a higher one.
          </div>
          <div flex align-center space-between>
            <div>Expire passwords after (days)</div>
            <paper-input id="passwordExpireDaysInput" class="slim-input" no-label-float disabled="[[!usePasswordExpiration]]"
              value="[[passwordExpireDays]]" type="number" min="7" max="[[maxPasswordExpireDays]]" on-value-changed="_setPasswordExpireDays"
            ></paper-input>
          </div>
          <div flex justify-end>
            <iron-collapse opened="[[_passwordExpirationHasChanged]]">
              <katapult-button size="18" color="var(--paper-grey-200)" on-click="resetPasswordExpirationChanges">Cancel</katapult-button>
              <katapult-button size="18" color="var(--secondary-color)" on-click="savePasswordExpirationChanges">Save Changes</katapult-button>
            </iron-collapse>
          </div>
        </div>
        <template is="dom-if" if="[[deactivateUsers]]">
          <div>
            <div flex align-center space-between>
              <div>User Deactivation Policy</div>
              <paper-toggle-button id="deactivateUsersInput" checked="[[deactivateUsersEnabled]]"
                on-click="toggleDeactivateUsers"
              ></paper-toggle-button>
            </div>
            <div class="option-description">Users will be deactivated after being inactive for a certain number of days</div>
            <div flex align-center space-between>
              <div>Deactivate after (days)</div>
              <paper-input id="deactivateUserDaysInput" class="slim-input" no-label-float disabled="[[!deactivateUsersEnabled]]"
                value="[[deactivateUserDays]]" type="number" on-value-changed="_setDeactivateUserDays"
              ></paper-input>
            </div>
            <div flex justify-end>
              <iron-collapse opened="[[_deactivateUsersHasChanged]]">
                <katapult-button size="18" color="var(--paper-grey-200)" on-click="resetDeactivateUsersChanges">Cancel</katapult-button>
                <katapult-button size="18" color="var(--secondary-color)" on-click="saveDeactivateUsersChanges">Save Changes</katapult-button>
              </iron-collapse>
            </div>
          </div>
        </template>
        <template is="dom-if" if="[[allowSignInMethods]]">
          <div>
            <div flex align-center space-between>
              <div>Sign-In Methods</div>
            </div>
            <div>
              <div class="option-description">
                The allowed sign-in methods for users in the company. Users will be able to sign in with any of the enabled methods.
              </div>
            </div>
            <div id="signInMethods" class="option-description">
              <template is="dom-if" if="[[allowEmailSignIn]]">
                <paper-toggle-button id="emailSignInOption" checked="[[emailSignInEnabled]]" on-click="toggleEnableSignInMethod" data-provider="Email">Email</paper-toggle-button>
              </template>
              <template is="dom-if" if="[[allowGoogleSignIn]]">
                <paper-toggle-button id="googleSignInOption" checked="[[googleSignInEnabled]]" on-click="toggleEnableSignInMethod" data-provider="Google">Google</paper-toggle-button>
              </template>
              <template is="dom-if" if="[[allowMicrosoftSignIn]]">
                <paper-toggle-button id="microsoftSignInOption" checked="[[microsoftSignInEnabled]]" on-click="toggleEnableSignInMethod" data-provider="Microsoft">Microsoft</paper-toggle-button>
              </template>
              <template is="dom-if" if="[[allowEnterpriseSsoSignIn]]">
                <paper-toggle-button id="enterpriseSsoSignInOption" checked="[[enterpriseSsoSignInEnabled]]" on-click="toggleEnableSignInMethod" data-provider="Enterprise SSO">Enterprise SSO</paper-toggle-button>
                <div style="padding-left: 30px; font-size: 13px;">
                  Provider: [[enterpriseSsoDescription.provider_name]]<br/>
                </div>
              </template>
          </div>
        </template>
      </div>
    </paper-header-content>
    `;
  }

  static get is() {
    return 'company-authentication-options';
  }

  static get properties() {
    return {
      _authenticationOptionsRef: {
        type: Object,
        computed: '_computeAuthenticationOptionsRef(companyId)',
        observer: '_updateAuthenticationOptionsListeners'
      },
      _passwordExpirationHasChanged: {
        type: Boolean,
        computed:
          '_computePasswordExpirationHasChanged(usePasswordExpiration, passwordExpireDays, _cachedUsePasswordExpiration, _cachedPasswordExpireDays)'
      },
      mfaAutoEnforced: {
        type: Boolean,
        computed: '_computeMfaAutoEnforced(utilityCompany, companyId)'
      },
      mfaEnabled: {
        type: Boolean,
        value: false
      },
      usePasswordExpiration: {
        type: Boolean,
        value: false
      },
      passwordExpireDays: {
        type: Number,
        value: ''
      },
      maxPasswordExpireDays: {
        type: Number,
        computed: '_computeMaxPasswordExpireDays(serverPasswordExpireTime)'
      },
      companyId: {
        type: String,
        value: null
      },
      utilityCompany: {
        type: String,
        value: null
      },
      serverPasswordExpireTime: {
        type: Number,
        value: null
      },
      allowSignInMethods: {
        type: Boolean,
        value: false
      },
      allowEmailSignIn: {
        type: Boolean,
        value: false
      },
      allowGoogleSignIn: {
        type: Boolean,
        value: false
      },
      allowMicrosoftSignIn: {
        type: Boolean,
        value: false
      },
      allowEnterpriseSsoSignIn: {
        type: Boolean,
        value: false
      },
      emailSignInEnabled: {
        type: Boolean,
        value: true
      },
      googleSignInEnabled: {
        type: Boolean,
        value: false
      },
      microsoftSignInEnabled: {
        type: Boolean,
        value: false
      },
      enterpriseSsoSignInEnabled: {
        type: Boolean,
        value: false
      },
      enterpriseSsoDescription: {
        type: Object,
        value: null
      },
      numSignInMethodsEnabled: {
        type: Number,
        value: null
      },
      deactivateUsers: {
        type: Boolean,
        value: false
      },
      _deactivateUsersHasChanged: {
        type: Boolean,
        computed:
          '_computeDeactivateUsersHasChanged(deactivateUsersEnabled, deactivateUserDays, _cachedDeactivateUsersEnabled, _cachedDeactivateUserDays)'
      }
    };
  }

  static get observers() {
    return [
      'calcNumSignInMethodsEnabled(companyId, emailSignInEnabled, googleSignInEnabled, microsoftSignInEnabled, enterpriseSsoSignInEnabled)'
    ];
  }

  ready() {
    super.ready();
  }

  _toDays(time) {
    return Convert(time, 'millis', 'days');
  }

  /** Confirms and toggles multi-factor authentication for the company */
  async promptToggleMfa(e) {
    const newValue = e.currentTarget.checked;
    // Quickly reset the toggle state back to what it was
    e.currentTarget.checked = !newValue;

    // Show a dialog asking the user to confirm their action
    const dialogConfig = this.mfaEnabled
      ? {
          title: 'Disable Multi-Factor Authentication',
          body: 'Are you sure you want to disable multi-factor authentication for all users?',
          confirmButton: { label: 'Disable', color: 'var(--paper-red-500)' },
          dialog: { modal: true }
        }
      : {
          title: 'Enable Multi-Factor Authentication',
          body: 'Are you sure you want to enable multi-factor authentication for all users?',
          confirmButton: { label: 'Enable' },
          dialog: { modal: true }
        };

    const dialog = KatapultDialog.confirm(dialogConfig);
    const didConfirm = await dialog.confirmed;

    // If the user confirmed, set the mfa option to the toggled value in the database
    if (didConfirm)
      this._authenticationOptionsRef
        .child('mfa')
        .set(newValue)
        .catch((err) => {
          KatapultDialog.alert({
            title: 'Error Enabling Multi-Factor Authentication',
            body: 'There was an error enabling multi-factor authentication. Please try again. If the problem persists, please contact support.',
            dialog: { color: 'var(--paper-red-500)', modal: true }
          });
        });
  }

  /** Sets password expiration inputs back to their cached values */
  resetPasswordExpirationChanges() {
    this.usePasswordExpiration = this._cachedUsePasswordExpiration;
    this.passwordExpireDays = this._cachedPasswordExpireDays;
  }

  resetDeactivateUsersChanges() {
    this.deactivateUsersEnabled = this._cachedDeactivateUsersEnabled;
    this.deactivateUserDays = this._cachedDeactivateUserDays;
  }

  /** Validate, confirm, and commit changes made to the password expiration policy */
  async savePasswordExpirationChanges() {
    const toggle = this.$.usePasswordExpirationInput;
    const input = this.$.passwordExpireDaysInput;
    let newPasswordExpireTime = 0;

    // If we are enabling password expiration, validate the input
    if (toggle.checked) {
      const passwordExpireDays = parseInt(input.value);
      if (isNaN(passwordExpireDays) || passwordExpireDays < 7 || passwordExpireDays > this.maxPasswordExpireDays) {
        KatapultDialog.alert({
          title: 'Invalid Password Expiration Time',
          body: `Password expiration time must be between 7 and ${this.maxPasswordExpireDays} days.`,
          dialog: { color: 'var(--paper-red-500)', modal: true }
        });
        input.invalid = true;
        return;
      }
      const passwordExpireMillis = Convert(passwordExpireDays, 'days', 'millis');
      newPasswordExpireTime = passwordExpireMillis;
    }

    // Ask the user to confirm their action
    const dialogConfig = KatapultDialog.confirm({
      title: 'Update Password Expiration Policy',
      body: 'Are you sure you want to update the password expiration policy for all users?',
      confirmButton: { label: 'Update' },
      dialog: { modal: true }
    });
    const didConfirm = await dialogConfig.confirmed;
    // If the user cancelled, return
    if (!didConfirm) return;

    // Convert the days to milliseconds and set the password expiration time
    this._authenticationOptionsRef
      .child('password_expire_time')
      .set(newPasswordExpireTime)
      .then(() => {
        input.invalid = false;
      })
      .catch((err) => {
        KatapultDialog.alert({
          title: 'Error Setting Password Expiration',
          body: 'There was an error setting the password expiration. Please try again. If the problem persists, please contact support.',
          dialog: { color: 'var(--paper-red-500)', modal: true }
        });
      });
  }

  async saveDeactivateUsersChanges() {
    const toggle = this.shadowRoot.querySelector('#deactivateUsersInput');
    const input = this.shadowRoot.querySelector('#deactivateUserDaysInput');
    let newDeactivateUserTime = 0;
    if (toggle.checked) {
      const deactivateUserDays = parseInt(input.value);
      if (isNaN(deactivateUserDays)) {
        KatapultDialog.alert({
          title: 'Invalid Deactivate User Time',
          body: 'Deactivate user time must be a number.',
          dialog: { color: 'var(--paper-red-500)', modal: true }
        });
        input.invalid = true;
        return;
      }
      const deactivateUserMillis = Convert(deactivateUserDays, 'days', 'millis');
      newDeactivateUserTime = deactivateUserMillis;
    }

    const dialogConfig = KatapultDialog.confirm({
      title: 'Update User Deactivation Policy',
      body: 'Are you sure you want to update the user deactivation policy for all users?',
      confirmButton: { label: 'Update' },
      dialog: { modal: true }
    });
    const didConfirm = await dialogConfig.confirmed;
    if (!didConfirm) return;

    this._authenticationOptionsRef
      .child('deactivate_user_time')
      .set(newDeactivateUserTime)
      .then(() => {
        input.invalid = false;
        CallCrossServerIdentityAction(globalThis.firebase.auth(), 'SYNC_DATA_TO_SERVERS', {
          companyId: this.selectedCompanyId
        });
      })
      .catch((err) => {
        KatapultDialog.alert({
          title: 'Error Setting User Deactivation',
          body: 'There was an error setting the user deactivation. Please try again. If the problem persists, please contact support.',
          dialog: { color: 'var(--paper-red-500)', modal: true }
        });
      });
  }

  toggleUsePasswordExpiration() {
    this.usePasswordExpiration = !this.usePasswordExpiration;
  }

  toggleDeactivateUsers() {
    this.deactivateUsersEnabled = !this.deactivateUsersEnabled;
  }

  /** Enables/disables whether users of the company can sign in using the given provider */
  async toggleEnableSignInMethod(e) {
    try {
      const newValue = e.currentTarget.checked;
      // Quickly reset the toggle state back to what it was
      e.currentTarget.checked = !newValue;

      // get the provider that is being toggled
      const provider = e.currentTarget.dataset.provider;
      // create a lookup table to check if the provider is enabled
      const providerEnabledLookup = {
        Email: this.emailSignInEnabled,
        Google: this.googleSignInEnabled,
        Microsoft: this.microsoftSignInEnabled,
        'Enterprise SSO': this.enterpriseSsoSignInEnabled
      };

      // Check whether the user is disabling all sign-in methods
      const disableAll = this.checkDisablingAllSignInMethods();

      // Show a dialog asking the user to confirm their action
      const dialogConfig = providerEnabledLookup[provider]
        ? {
            dialog: {
              title: `Disable ${provider} Sign-In`,
              color: disableAll ? 'var(--paper-red-500)' : null,
              modal: true
            },
            body: disableAll
              ? `You are disabling all sign-in methods. Your company's users will not be able to sign in to Katapult Pro. Are you sure you want to disable all sign-in methods?`
              : `Are you sure you want to disable ${provider} sign-in for all users?`,
            confirmButton: { label: disableAll ? 'Disable All' : 'Disable', color: 'var(--paper-red-500)' }
          }
        : {
            title: `Enable ${provider} Sign-In`,
            body: `Are you sure you want to enable ${provider} sign-in for all users?`,
            confirmButton: { label: 'Enable' },
            dialog: { modal: true }
          };

      const dialog = KatapultDialog.confirm(dialogConfig);
      const didConfirm = await dialog.confirmed;

      // If the user confirmed, set the sign-in option to the toggled value in the database
      if (didConfirm)
        this._authenticationOptionsRef
          .child(`sign_in_options/${snakeCase(provider)}`)
          .set(newValue)
          .catch((err) => {
            KatapultDialog.alert({
              title: `Error Enabling ${provider} Sign-In`,
              body: `There was an error enabling ${provider} sign-in. Please try again. If the problem persists, please contact support.`,
              dialog: { color: 'var(--paper-red-500)', modal: true }
            });
          });
    } catch (err) {
      console.error(err);
    }
  }

  /** Checks if the user is disabling all sign-in methods */
  checkDisablingAllSignInMethods() {
    if (this.numSignInMethodsEnabled == 1) return true;
    else if (this.numSignInMethodsEnabled == null) throw new Error('Error loading sign-in methods.');
    return false;
  }

  /** Compute function that returns a ref to company options */
  _computeAuthenticationOptionsRef(companyId) {
    return companyId != null ? FirebaseWorker.database().ref(`photoheight/company_space/${companyId}/options/authentication`) : null;
  }

  _computeMaxPasswordExpireDays(serverPasswordExpireTime) {
    return serverPasswordExpireTime != null ? Convert(serverPasswordExpireTime, 'millis', 'days') : 365;
  }

  /** Compute whether or not the mfa toggle should be visually locked to "enabled" */
  _computeMfaAutoEnforced(utilityCompany, companyId) {
    // IMPROVEMENT: This logic may need to be expanded upon in the future
    return utilityCompany === companyId;
  }

  /** Compute function that returns whether or not the password expiration policy has been changed locally */
  _computePasswordExpirationHasChanged(usePasswordExpiration, passwordExpireDays) {
    const passwordExpirationHasChanged = usePasswordExpiration != this._cachedUsePasswordExpiration;
    const passwordExpireDaysHasChanged = passwordExpireDays != this._cachedPasswordExpireDays;
    return passwordExpirationHasChanged || passwordExpireDaysHasChanged;
  }

  _computeDeactivateUsersHasChanged(deactivateUsersEnabled, deactivateUserDays) {
    const deactivateUsersHasChanged = deactivateUsersEnabled != this._cachedDeactivateUsersEnabled;
    const deactivateUserDaysHasChanged = deactivateUserDays != this._cachedDeactivateUserDays;
    return deactivateUsersHasChanged || deactivateUserDaysHasChanged;
  }

  _setPasswordExpireDays(e) {
    const days = parseInt(e.detail.value);
    this.passwordExpireDays = !isNaN(days) ? days : '';
  }

  _setDeactivateUserDays(e) {
    const days = parseInt(e.detail.value);
    this.deactivateUserDays = !isNaN(days) ? days : '';
  }

  /** Updates this listeners for the various authentication options */
  _updateAuthenticationOptionsListeners(newAuthOptionsRef, oldAuthOptionsRef) {
    // Remove the old listeners, if they exist
    if (oldAuthOptionsRef) {
      oldAuthOptionsRef.child('mfa').off('value', this._mfaRefListener);
      oldAuthOptionsRef.child('password_expire_time').off('value', this._passwordExpireTimeRefListener);
      oldAuthOptionsRef.child('sign_in_options/email').off('value', this._emailSignInRefListener);
      oldAuthOptionsRef.child('sign_in_options/google').off('value', this._googleSignInRefListener);
      oldAuthOptionsRef.child('sign_in_options/microsoft').off('value', this._microsoftSignInRefListener);
      oldAuthOptionsRef.child('sign_in_options/enterprise_sso').off('value', this._enterpriseSsoSignInRefListener);
      oldAuthOptionsRef.child('enterprise_sso').off('value', this._enterpriseSsoDescriptionRefListener);
    }

    // Add the new listeners, if they exist
    // IMPROVEMENT: If we add more auth settings here some day, we can probably just have a single listener for the whole object
    if (newAuthOptionsRef) {
      this._mfaRefListener = newAuthOptionsRef.child('mfa').on('value', (snapshot) => {
        this.mfaEnabled = snapshot.val();
      });
      this._passwordExpireTimeRefListener = newAuthOptionsRef.child('password_expire_time').on('value', (snapshot) => {
        const val = snapshot.val();
        const days = val ? Convert(val, 'millis', 'days') : '';
        this.usePasswordExpiration = this._cachedUsePasswordExpiration = snapshot.exists() && val !== 0;
        this.passwordExpireDays = this._cachedPasswordExpireDays = days;
      });
      this._setUserDeactivateTimeRefListener = newAuthOptionsRef.child('deactivate_user_time').on('value', (snapshot) => {
        const val = snapshot.val();
        const days = val ? Convert(val, 'millis', 'days') : '';
        this.deactivateUsersEnabled = this._cachedDeactivateUsersEnabled = snapshot.exists() && val !== 0;
        this.deactivateUserDays = this._cachedDeactivateUserDays = days;
      });
      this._emailSignInRefListener = newAuthOptionsRef.child('sign_in_options/email').on('value', (snapshot) => {
        // Check that the snapshot exists before setting the value. We want this to set the value to true by default if the company doesn't have the setting in Firebase
        if (snapshot.val() !== null) this.emailSignInEnabled = snapshot.val();
      });
      this._googleSignInRefListener = newAuthOptionsRef.child('sign_in_options/google').on('value', (snapshot) => {
        this.googleSignInEnabled = snapshot.val();
      });
      this._microsoftSignInRefListener = newAuthOptionsRef.child('sign_in_options/microsoft').on('value', (snapshot) => {
        this.microsoftSignInEnabled = snapshot.val();
      });
      this._enterpriseSsoSignInRefListener = newAuthOptionsRef.child('sign_in_options/enterprise_sso').on('value', (snapshot) => {
        this.enterpriseSsoSignInEnabled = snapshot.val();
      });
      this._enterpriseSsoDescriptionRefListener = newAuthOptionsRef.child('enterprise_sso').on('value', (snapshot) => {
        this.enterpriseSsoDescription = snapshot.val();
      });
    }
  }

  /** Calculates the number of sign-in methods a company has enabled */
  calcNumSignInMethodsEnabled(companyId, emailSignInEnabled, googleSignInEnabled, microsoftSignInEnabled, enterpriseSsoSignInEnabled) {
    if (!companyId) return;

    FirebaseWorker.database()
      .ref(`photoheight/company_space/${companyId}/options/authentication/sign_in_options`)
      .once('value')
      .then((snapshot) => {
        const val = snapshot.val() || {};
        let count = Object.values(val).filter((v) => v === true).length || 0;

        // Legacy check if the email key doesn't exist in Firebase, but it's really enabled
        if (emailSignInEnabled && val.email == null) count++;

        // Set the number of sign-in methods enabled
        this.numSignInMethodsEnabled = count;
      })
      .catch((err) => {
        console.error(err);
      });
  }
}

customElements.define(CompanyAuthenticationOptions.is, CompanyAuthenticationOptions);
